import PersonAvatar from "@common-components/tasks/person-avatar/person-avatar";
import { namespace } from "@common-config/constants";
import { useAppDispatch, useAppSelector } from "@common-reducers/hooks/store.hook";
import { selectUserObject } from "@common-reducers/UserSelectors";
import { logoutWrapperThunk, toggleDarkMode } from "@common-reducers/UserThunks";
import { selectIsDarkMode } from "@common-reducers/UserSelectors";
import { useRouter } from "@common-reducers/hooks/use-router";
import BackupOutlinedIcon from "@mui/icons-material/BackupOutlined";
import CampaignOutlinedIcon from "@mui/icons-material/CampaignOutlined";
import DarkModeOutlinedIcon from "@mui/icons-material/DarkModeOutlined";
import LightModeOutlinedIcon from "@mui/icons-material/LightModeOutlined";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import GppGoodOutlinedIcon from "@mui/icons-material/GppGoodOutlined";
import LogoutOutlinedIcon from "@mui/icons-material/LogoutOutlined";
import PersonOutlineRoundedIcon from "@mui/icons-material/PersonOutlineRounded";
import SettingsIcon from "@mui/icons-material/Settings";
import { Box, Divider, IconButton, Menu, MenuItem, Typography } from "@mui/material";
import { useState } from "react";
import { useTranslation } from "react-i18next";

/**
 * The pages reachable inside the clear flow. Labels and icons are the SAME keys the
 * real sidebar uses for these routes, so the two surfaces never drift apart.
 *
 * `adminOnly` mirrors the sidebar's gate on the backup page (support/admin only);
 * AccountBackupView guards direct navigation independently.
 */
const CLEAR_DESTINATIONS = [
  { path: "/clear-calendar", labelKey: "calendar.title", Icon: CalendarMonthOutlinedIcon },
  { path: "/clear-campaigns", labelKey: "navigation.launchCampaign", Icon: CampaignOutlinedIcon },
  { path: "/clear-account", labelKey: "sideBar.personalInformation", Icon: PersonOutlineRoundedIcon },
  { path: "/clear-account-security", labelKey: "sideBar.security", Icon: GppGoodOutlinedIcon },
  { path: "/clear-connections", labelKey: "sideBar.whatsAppConnections", Icon: SettingsIcon },
  { path: "/clear-account-backup", labelKey: "sideBar.backup", Icon: BackupOutlinedIcon, adminOnly: true },
] as const;

/**
 * The only account affordance on a chromeless ("clear") page.
 *
 * The clear flow hides the sidebar, and with it the account footer that normally
 * shows who is signed in and offers Log out — leaving no way to identify or leave
 * the session. This restores just those two things.
 *
 * It is also the only navigation between clear pages, since the sidebar that
 * normally links them is gone.
 *
 * Deliberately NOT a reuse of SmallUserMenu: that menu links out to the full
 * product (Invite people, Upgrade, Switch workspace), which is exactly what the
 * clear flow exists to keep out of reach.
 *
 * Positioned at the bottom INLINE-START corner: the add-message FAB and the Crisp
 * bubble both sit at the inline-end corner, so this stays clear of them in LTR and
 * RTL alike.
 */
export default function ClearUserMenu() {
  const { t } = useTranslation(namespace);
  const dispatch = useAppDispatch();
  const router = useRouter();
  const user = useAppSelector(selectUserObject);
  const accessToken = useAppSelector((state) => state.UserReducer.accessToken);
  const isDarkMode = useAppSelector(selectIsDarkMode);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  if (!user) return null;

  const isBackupAdmin = user.role === "support" || user.role === "admin";
  const currentPath = window.location.pathname;
  // Offer every clear page except the one you are already on.
  const destinations = CLEAR_DESTINATIONS.filter(
    (destination) =>
      destination.path !== currentPath && (!("adminOnly" in destination && destination.adminOnly) || isBackupAdmin),
  );

  const goTo = (path: string) => {
    setAnchorEl(null);
    router.navigate(path);
  };

  const handleLogout = () => {
    setAnchorEl(null);
    // logoutWrapperThunk navigates to the login screen for the CURRENT flow
    // (see flowPath in UserThunks), so a clear session lands on /clear-login.
    dispatch(logoutWrapperThunk({ accessToken: accessToken ?? null }));
  };

  // Same thunk the full product's menu uses, so the choice persists on the user
  // and follows them across surfaces — this is a second entry point, not a
  // second setting.
  const toggleTheme = () => {
    setAnchorEl(null);
    dispatch(toggleDarkMode());
  };

  const displayName = [user.firstName, user.lastName].filter(Boolean).join(" ").trim();

  return (
    <>
      <IconButton
        onClick={(event) => setAnchorEl(event.currentTarget)}
        aria-label="account"
        data-testid="clear-user-menu-button"
        sx={{
          position: "fixed",
          bottom: 16,
          insetInlineStart: 16,
          // Above the scheduled-list container, which sets z-index 10000 (shared
          // code, used by the real sidebar too) and otherwise swallows the clicks
          // — the chip sits over the panel in the clear calendar.
          zIndex: 10050,
          padding: "5px", // 34px avatar + padding + border = 46px, over the 44px tap-target minimum
          backgroundColor: "var(--color-surface, #fff)",
          border: "1px solid #E9E7FB",
          boxShadow: anchorEl
            ? "0 6px 20px rgba(99, 102, 241, 0.35)"
            : "0 2px 10px rgba(49, 46, 129, 0.14)",
          transition: "box-shadow 0.18s ease, transform 0.18s ease",
          "&:hover": {
            backgroundColor: "var(--color-surface, #fff)",
            transform: "translateY(-1px)",
            boxShadow: "0 6px 18px rgba(99, 102, 241, 0.3)",
          },
        }}
      >
        <PersonAvatar
          size={34}
          email={user.email}
          profileImg={user.profileImg}
          firstName={user.firstName}
          lastName={user.lastName}
          /* PersonAvatar otherwise picks a colour by hashing the email against a
             shared palette — an identity cue that reads as a stray colour on the
             single account chip. Pin it to the flow's brand token instead. */
          bgcolor="var(--cta-button-bg)"
        />
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "top", horizontal: "left" }}
        transformOrigin={{ vertical: "bottom", horizontal: "left" }}
        sx={{ zIndex: 10060 }}
        slotProps={{
          paper: {
            elevation: 0,
            sx: {
              minWidth: 264,
              marginBlockEnd: 1.5,
              // The menu's own colours are a fixed light set (headers, item text,
              // borders), so pin the surface too — inheriting the theme's dark
              // paper would leave dark text on a dark background.
              backgroundColor: "#fff",
              borderRadius: "16px",
              border: "1px solid #E9E7FB",
              boxShadow: "0 12px 40px rgba(49, 46, 129, 0.16)",
              overflow: "hidden",
              "& .MuiList-root": { padding: 0 },
            },
          },
        }}
      >
        {/* Identity header — who you are signed in as, at a glance. */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.25,
            paddingInline: 2,
            paddingBlock: 1.75,
            background: "linear-gradient(135deg, #EEF2FF 0%, #F5F3FF 100%)",
            borderBottom: "1px solid #EDEBFB",
          }}
        >
          <PersonAvatar
            size={38}
            email={user.email}
            profileImg={user.profileImg}
            firstName={user.firstName}
            lastName={user.lastName}
            bgcolor="var(--cta-button-bg)"
          />
          <Box sx={{ minWidth: 0 }}>
            {displayName && (
              <Typography
                sx={{
                  fontSize: 14,
                  fontWeight: 600,
                  color: "#312E81",
                  lineHeight: 1.3,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {displayName}
              </Typography>
            )}
            <Typography
              sx={{
                fontSize: 12,
                color: "#6E6A97",
                lineHeight: 1.4,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                maxWidth: 190,
              }}
            >
              {user.email}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ paddingBlock: 0.75 }}>
          {destinations.map(({ path, labelKey, Icon }) => (
            <MenuItem
              key={path}
              onClick={() => goTo(path)}
              data-testid={`${path.replace(/^\//, "")}-link`}
              sx={{
                marginInline: 1,
                paddingInline: 1.25,
                paddingBlock: 1,
                borderRadius: "10px",
                fontSize: 14,
                color: "#3F3B6B",
                gap: 1.25,
                "&:hover": { backgroundColor: "#EEF2FF", color: "#4338CA" },
                "&:hover .MuiSvgIcon-root": { color: "#4F46E5" },
              }}
            >
              <Icon sx={{ fontSize: 18, color: "#8F8BB8", transition: "color 0.15s ease" }} />
              {t(labelKey)}
            </MenuItem>
          ))}
        </Box>

        <Divider sx={{ borderColor: "#EDEBFB", margin: 0 }} />

        <Box sx={{ paddingBlock: 0.75 }}>
          <MenuItem
            onClick={toggleTheme}
            data-testid="clear-theme-toggle"
            sx={{
              marginInline: 1,
              paddingInline: 1.25,
              paddingBlock: 1,
              borderRadius: "10px",
              fontSize: 14,
              color: "#3F3B6B",
              gap: 1.25,
              "&:hover": { backgroundColor: "#EEF2FF", color: "#4338CA" },
              "&:hover .MuiSvgIcon-root": { color: "#4F46E5" },
            }}
          >
            {isDarkMode
              ? <LightModeOutlinedIcon sx={{ fontSize: 18, color: "#8F8BB8", transition: "color 0.15s ease" }} />
              : <DarkModeOutlinedIcon sx={{ fontSize: 18, color: "#8F8BB8", transition: "color 0.15s ease" }} />}
            {isDarkMode
              ? t("smallUserMenu.changeToLightMode", "Light mode")
              : t("smallUserMenu.changeToDarkMode", "Dark mode")}
          </MenuItem>
        </Box>

        <Divider sx={{ borderColor: "#EDEBFB", margin: 0 }} />

        <Box sx={{ paddingBlock: 0.75 }}>
          <MenuItem
            onClick={handleLogout}
            data-testid="clear-logout-button"
            sx={{
              marginInline: 1,
              paddingInline: 1.25,
              paddingBlock: 1,
              borderRadius: "10px",
              fontSize: 14,
              color: "#B4436C",
              gap: 1.25,
              "&:hover": { backgroundColor: "#FDF2F6", color: "#9F1239" },
              "&:hover .MuiSvgIcon-root": { color: "#9F1239" },
            }}
          >
            <LogoutOutlinedIcon sx={{ fontSize: 18, color: "#C98BA5", transition: "color 0.15s ease" }} />
            {t("smallUserMenu.logOut")}
          </MenuItem>
        </Box>
      </Menu>
    </>
  );
}
